import React, { useEffect, useState } from "react";
import { api, brl } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";
import { toast } from "sonner";
import { X, Copy, ArrowSquareOut, ArrowsClockwise, QrCode, WarningCircle } from "@phosphor-icons/react";

const STATUS_LABEL = {
  criado: { label: "Criado", cls: "neutral" },
  aguardando_pagamento: { label: "Aguardando pagamento", cls: "medio" },
  pago: { label: "Pago", cls: "green" },
  expirado: { label: "Expirado", cls: "risco" },
  cancelado: { label: "Cancelado", cls: "red" },
  erro: { label: "Erro", cls: "red" },
};

function ChargeStatus({ status }) {
  const s = STATUS_LABEL[status] || { label: status, cls: "neutral" };
  return <span className={`badge ${s.cls}`} data-testid="pix-status-badge">{s.label}</span>;
}

// Modal de cobranca PIX real (Mercado Pago). Gera/recupera a cobranca da parcela,
// mostra QR Code, PIX copia-e-cola, link de pagamento e status, e permite atualizar.
export default function PixChargeModal({ installmentId, open, onClose, onSettled }) {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [charge, setCharge] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !installmentId) return;
    setCharge(null); setError(""); setLoading(true);
    (async () => {
      try {
        const { data } = await api.post(`/pix/charge/${installmentId}`);
        setCharge(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Nao foi possivel gerar a cobranca PIX.");
      } finally { setLoading(false); }
    })();
  }, [open, installmentId]);

  const copyCode = async () => {
    if (!charge?.copy_paste) return;
    const ok = await copyToClipboard(charge.copy_paste);
    ok ? toast.success("PIX copia e cola copiado!") : toast.error("Falha ao copiar");
  };

  const refresh = async (silent = false) => {
    if (!charge?.id) return null;
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.post(`/pix/refresh/${charge.id}`);
      setCharge(data);
      if (data.status === "pago") {
        toast.success("Pagamento confirmado!");
        onSettled && onSettled();
      } else if (!silent) {
        toast.info("Status atualizado: " + (STATUS_LABEL[data.status]?.label || data.status));
      }
      return data.status;
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || "Erro ao atualizar status");
      return null;
    } finally { if (!silent) setRefreshing(false); }
  };

  // Polling automatico: consulta o status a cada 5s enquanto aguarda pagamento.
  useEffect(() => {
    if (!open || !charge?.id || !charge?.mp_payment_id) return;
    if (charge.status !== "aguardando_pagamento" && charge.status !== "criado") return;
    const t = setInterval(async () => {
      const st = await refresh(true);
      if (st && st !== "aguardando_pagamento" && st !== "criado") clearInterval(t);
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, charge?.id, charge?.status]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" onClick={onClose}>
      <div className="bg-white border border-[#EBE5DF] max-w-md w-full p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pix-charge-modal">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Pagamento PIX · Mercado Pago</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Cobranca da parcela</h2>
          </div>
          <button onClick={onClose} className="text-[#5C5A57] hover:text-black" data-testid="close-pix-modal-btn"><X size={20} /></button>
        </div>

        {loading && (
          <div className="py-12 text-center text-sm tracking-widest uppercase text-[#5C5A57]" data-testid="pix-loading">
            <QrCode size={40} weight="duotone" className="mx-auto mb-3 animate-pulse" /> Gerando cobranca PIX real...
          </div>
        )}

        {!loading && error && (
          <div className="py-6" data-testid="pix-error">
            <div className="flex items-start gap-2 p-4 bg-[#F4E6E5] border border-[#DDA7A5] text-sm text-[#7A2D2B]">
              <WarningCircle size={20} weight="duotone" className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {!loading && charge && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Valor</div>
                <div className="font-display text-3xl font-bold tabular">{brl(charge.amount)}</div>
              </div>
              <ChargeStatus status={charge.status} />
            </div>

            {charge.qr_code_data_url && (
              <div className="grid place-items-center">
                <img src={charge.qr_code_data_url} alt="QR Code PIX" className="w-56 h-56 border border-[#EBE5DF]" data-testid="pix-qr-image" />
              </div>
            )}

            {charge.copy_paste && (
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">PIX copia e cola</div>
                <textarea readOnly value={charge.copy_paste} className="w-full h-20 text-[11px] font-mono border border-[#EBE5DF] p-2 resize-none" data-testid="pix-copy-paste" />
                <button onClick={copyCode} className="brand w-full mt-2" data-testid="copy-pix-code-btn"><Copy size={14} className="inline mr-2" />Copiar codigo PIX</button>
              </div>
            )}

            <div className="text-xs text-[#5C5A57] space-y-1 border-t border-[#EBE5DF] pt-3">
              {charge.mp_payment_id && <div>ID Mercado Pago: <span className="tabular">{charge.mp_payment_id}</span></div>}
              {charge.expires_at && <div>Valido ate: {new Date(charge.expires_at).toLocaleString("pt-BR")}</div>}
            </div>

            <div className="flex flex-wrap gap-3 pt-1">
              {charge.ticket_url && (
                <a href={charge.ticket_url} target="_blank" rel="noopener noreferrer" className="brand" style={{ background: "#5C5A57" }} data-testid="open-pix-link-btn">
                  <ArrowSquareOut size={14} className="inline mr-2" />Abrir pagamento
                </a>
              )}
              <button onClick={() => refresh()} disabled={refreshing} className="brand disabled:opacity-50" data-testid="refresh-pix-status-btn">
                <ArrowsClockwise size={14} className={`inline mr-2 ${refreshing ? "animate-spin" : ""}`} />Atualizar status
              </button>
            </div>

            {(charge.status === "aguardando_pagamento" || charge.status === "criado") && (
              <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] text-center" data-testid="pix-auto-check">
                Verificando o pagamento automaticamente...
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
